import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { RefreshCw, Loader2, Send } from 'lucide-react';

import { fetchTodayDigest, regenerateDigest, executeDigest } from '../../../redux/DigestSlice/digestSlice';
import './digest-design.css';

/**
 * Regenerate + Execute, rendered in the top bar on /daily-digest only.
 *
 * These live in the navbar rather than the page body because that is where the
 * design puts them — `Frame`'s `actions` slot — and because Execute is the one
 * control that spends money: it should sit in a fixed place, not scroll away
 * behind a queue of 55 rows.
 *
 * The component owns the whole interaction (state, confirm, dispatch, refetch)
 * instead of taking callbacks from the page. Navbaar is mounted globally and
 * the page is not its parent, so passing handlers down would have meant either
 * a context or a second copy of the confirm — and a second copy of a
 * spend-real-credits confirmation is exactly the thing that drifts.
 */
const DigestNavActions = () => {
    const dispatch = useDispatch();
    const { digest, loading, executing } = useSelector((state) => state.digest);

    const actions = useMemo(() => digest?.actions || [], [digest]);

    // Execute only ever touches fetch_contact rows (see actionExecutor.js), so
    // the label, the price and the confirm are all scoped to those alone.
    const approvedFetch = useMemo(
        () => actions.filter((a) => a.status === 'approved' && a.kind === 'fetch_contact'),
        [actions]
    );
    const contacts = approvedFetch.reduce((s, a) => s + (a.contactsRequested || 0), 0);
    const credits = approvedFetch.reduce((s, a) => s + (a.creditCost || 0), 0);

    const handleExecute = async () => {
        if (!digest || approvedFetch.length === 0 || executing) return;
        // Confirm with the exact numbers rather than a generic "are you sure?" —
        // nobody should approve rows and only then discover the price.
        const confirmed = window.confirm(
            `This will fetch up to ${contacts} contact${contacts === 1 ? '' : 's'} `
            + `for ${credits} credit${credits === 1 ? '' : 's'}. Continue?`
        );
        if (!confirmed) return;

        const result = await dispatch(executeDigest({ digestId: digest._id }));
        if (!executeDigest.fulfilled.match(result)) return;

        const summary = result.payload || {};
        const fetched = summary.contactsFetched || 0;
        const owned = summary.alreadyOwned || 0;
        toast.success(
            `Fetched ${fetched} contact${fetched === 1 ? '' : 's'} `
            + `for ${summary.creditsSpent || 0} credit${summary.creditsSpent === 1 ? '' : 's'}.`
            // Without this the run reads as a mis-charge: the row was priced at
            // 20 and cost 6, with nothing on screen explaining the difference.
            + (owned ? ` ${owned} were already in your contacts — not charged.` : '')
        );

        if (fetched > 0) {
            /**
             * REBUILD, don't just refetch.
             *
             * The contacts that were just bought only become outreach rows when
             * the digest is PLANNED against them — `fetchTodayDigest` calls
             * buildDigest without `force`, which returns the stored digest
             * untouched, so the people you just paid for would not appear until
             * tomorrow's run. A forced rebuild drafts them into today's digest
             * immediately.
             *
             * Safe to do automatically only because buildDigest now carries
             * approved and skipped rows through a rebuild (see
             * PRESERVED_ACTION_STATUSES) — otherwise every Execute would reset
             * the user's triage on everything it did not execute.
             */
            toast.info('Adding the new contacts to today\u2019s digest\u2026', { autoClose: 2500 });
            await dispatch(regenerateDigest());
        } else {
            // Nothing arrived, so there is nothing new to plan against — skip
            // the rebuild and its LLM drafting cost.
            dispatch(fetchTodayDigest());
        }
    };

    return (
        // cc-scope so the ported button tokens resolve here: the navbar sits
        // outside the page subtree that normally carries them.
        // hidden below sm to match the Credits/Profile page actions beside it —
        // the bar has no room for two buttons on a phone.
        <span className="cc-scope hidden sm:inline-flex" style={{ alignItems: 'center', gap: 8 }}>
            <button
                type="button"
                className="cc-btn cc-btn-secondary cc-btn-sm"
                onClick={() => dispatch(regenerateDigest())}
                disabled={loading}
            >
                <RefreshCw style={{ width: 12, height: 12 }} className={loading ? 'animate-spin' : ''} />
                Regenerate
            </button>
            <button
                type="button"
                className="cc-btn cc-btn-primary cc-btn-sm"
                onClick={handleExecute}
                disabled={approvedFetch.length === 0 || executing}
                title={approvedFetch.length === 0 ? 'Approve at least one contact-fetch row first' : ''}
            >
                {executing
                    ? <Loader2 style={{ width: 12, height: 12 }} className="animate-spin" />
                    : <Send style={{ width: 12, height: 12 }} />}
                {approvedFetch.length > 0
                    ? `Execute ${approvedFetch.length} approved · ${credits} credits`
                    : 'Execute approved'}
            </button>
        </span>
    );
};

export default DigestNavActions;
